import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { withRouter } from '../../common/withRouter';
import { LayoutContext } from '../layout/siteLayout';
import PageTop from '../layout/PageTop';
import withApi from '../higherOrderComponents/withApi';

const PAGE_SIZE = 50;

const statBoxStyle = {
  backgroundColor: 'white',
  border: '1px solid #e5e5e5',
  padding: '1rem 1.2rem',
  minWidth: '10rem',
};

const cellStyle = { padding: '0.4rem 0.6rem', borderBottom: '1px solid #eee', textAlign: 'left' };

const formatDate = (value) => (value ? new Date(value).toLocaleString('da-DK') : '');

// Marketing-email consent overview: who said yes, and how many of the users
// who booked since the checkout checkbox launched (25 Aug 2026) said yes.
class MarketingConsentsInternal extends React.Component {
  constructor() {
    super();

    this.state = {
      stats: null,
      list: null,
      consented: true,
      page: 1,
      error: null,
    };
  }

  componentDidMount() {
    this.loadStats();
    this.loadList();
  }

  async loadStats() {
    const { api: { marketingConsent: { getMarketingConsentStats } } } = this.props;
    const response = await getMarketingConsentStats();
    if (response.success) {
      this.setState({ stats: response.data });
    } else {
      this.setState({ error: 'Unable to load stats' });
    }
  }

  async loadList() {
    const { api: { marketingConsent: { listMarketingConsents } } } = this.props;
    const { consented, page } = this.state;
    const response = await listMarketingConsents(consented, page, PAGE_SIZE);
    if (response.success) {
      this.setState({ list: response.data });
    } else {
      this.setState({ error: 'Unable to load list' });
    }
  }

  changeFilter(consented) {
    this.setState({ consented, page: 1, list: null }, () => this.loadList());
  }

  changePage(page) {
    this.setState({ page, list: null }, () => this.loadList());
  }

  renderStats() {
    const { stats } = this.state;
    if (!stats) {
      return <p>Loading stats…</p>;
    }

    const boxes = [
      { label: 'Said yes (bookers since 25 Aug)', value: `${stats.percentOfBookers}%`, hint: `${stats.consentedBookers} of ${stats.bookersSinceLaunch} users who booked` },
      { label: 'Consented now', value: stats.consented },
      { label: 'Not consented (row exists)', value: stats.notConsented, hint: 'unticked, withdrawn or reset' },
    ];

    return (
      <React.Fragment>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', marginBottom: '1rem' }}>
          {boxes.map((box) => (
            <div key={box.label} style={statBoxStyle}>
              <div style={{ color: '#666', fontSize: '0.85em' }}>{box.label}</div>
              <div style={{ fontSize: '1.6em', fontWeight: 'bold' }}>{box.value}</div>
              {box.hint && <div style={{ color: '#999', fontSize: '0.8em' }}>{box.hint}</div>}
            </div>
          ))}
        </div>
        <p style={{ color: '#666', fontSize: '0.9em' }}>
          By source: {Object.entries(stats.bySource || {}).map(([k, v]) => `${k} ${v}`).join(' · ') || '—'}
          <br />
          By week: {Object.entries(stats.byWeek || {}).map(([k, v]) => `${k}: ${v}`).join(' · ') || '—'}
        </p>
      </React.Fragment>
    );
  }

  renderList() {
    const { list, consented, page } = this.state;

    return (
      <React.Fragment>
        <div style={{ margin: '1rem 0' }}>
          <label style={{ marginRight: '1rem' }}>
            <input type="radio" checked={consented} onChange={() => this.changeFilter(true)} /> Consented
          </label>
          <label>
            <input type="radio" checked={!consented} onChange={() => this.changeFilter(false)} /> Not consented
          </label>
        </div>
        {!list ? <p>Loading…</p> : (
          <React.Fragment>
            <p>{list.total} users</p>
            <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: 'white' }}>
              <thead>
                <tr>
                  <th style={cellStyle}>Name</th>
                  <th style={cellStyle}>Email</th>
                  <th style={cellStyle}>Consented at</th>
                  <th style={cellStyle}>Source</th>
                  <th style={cellStyle}>Version</th>
                  <th style={cellStyle}>Last change</th>
                </tr>
              </thead>
              <tbody>
                {list.items.map((item) => (
                  <tr key={item.userId}>
                    <td style={cellStyle}><Link to={`/user/${item.userId}`}>{item.name || item.userId}</Link></td>
                    <td style={cellStyle}>{item.email}</td>
                    <td style={cellStyle}>{formatDate(item.consentedAtUtc)}</td>
                    <td style={cellStyle}>{item.consentSource}</td>
                    <td style={cellStyle}>{item.consentVersion}</td>
                    <td style={cellStyle}>{formatDate(item.updatedAtUtc)} {item.updatedBy && `(${item.updatedBy})`}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ margin: '1rem 0', display: 'flex', gap: '1rem', alignItems: 'center' }}>
              <button type="button" disabled={page <= 1} onClick={() => this.changePage(page - 1)}>Previous</button>
              <span>Page {page} of {Math.max(1, Math.ceil(list.total / PAGE_SIZE))}</span>
              <button type="button" disabled={page * PAGE_SIZE >= list.total} onClick={() => this.changePage(page + 1)}>Next</button>
            </div>
          </React.Fragment>
        )}
      </React.Fragment>
    );
  }

  render() {
    const { error } = this.state;

    return (
      <LayoutContext.Consumer>
        {() => (
          <React.Fragment>
            <PageTop
              documentTitle='Marketing consents'
              documentDescription='Marketing consents'
            />
            <h2 className='main-page-header'>
              Marketing consents
            </h2>
            {error && <p style={{ color: 'red' }}>{error}</p>}
            {this.renderStats()}
            {this.renderList()}
          </React.Fragment>
        )}
      </LayoutContext.Consumer>
    );
  }
}

const MarketingConsents = withRouter(withApi(
  connect(({ user }) => ({ isLoggedIn: !!user }), {})(MarketingConsentsInternal)
));

MarketingConsents.requiresLogin = true;
MarketingConsents.propTypes = { isLoggedIn: PropTypes.bool };

export default MarketingConsents;
